import { useCallback, useEffect, useState } from 'react';
import { subscriptions as subsApi, tenants as tenantsApi, type Subscription } from '../api';
import { formatMoney, formatDate } from '../lib/format';
import { useToast } from '../lib/toast';
import { Modal, Field, inputCls } from '../lib/Modal';

export default function SubscriptionsPage() {
  const { push } = useToast();
  const [data, setData] = useState<Subscription[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [overrideFor, setOverrideFor] = useState<Subscription | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await subsApi.list({ page, limit: 25, search: search || undefined, status: status || undefined });
      setData(res.data);
      setTotal(res.total);
      setTotalPages(res.totalPages);
    } catch (e) { setError((e as Error).message); }
  }, [page, search, status]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold">Subscriptions</h1>
        <div className="text-sm text-ink-muted">{total} total</div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4">
        <input placeholder="Search customer" value={search}
               onChange={e => { setSearch(e.target.value); setPage(1); }}
               className="flex-1 bg-ink-bg border border-ink-border text-ink-text rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-600" />
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}
                className="bg-ink-bg border border-ink-border text-ink-text rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-600">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="trialing">Trialing</option>
          <option value="past_due">Past due</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {error && <div className="bg-red-950/50 border border-red-900 text-red-300 rounded p-3 text-sm mb-4">{error}</div>}
      {data === null && !error && <div className="text-ink-muted text-sm">Loading…</div>}
      {data && data.length === 0 && (
        <div className="bg-ink-card border border-ink-border rounded p-8 text-center text-ink-muted text-sm">
          No subscriptions match.
        </div>
      )}

      {data && data.length > 0 && (
        <div className="bg-ink-card border border-ink-border rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-bg border-b border-ink-border text-xs uppercase text-ink-muted">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Customer</th>
                <th className="text-left px-4 py-2 font-semibold">Product</th>
                <th className="text-left px-4 py-2 font-semibold">Tier</th>
                <th className="text-left px-4 py-2 font-semibold">Status</th>
                <th className="text-right px-4 py-2 font-semibold">Price / mo</th>
                <th className="text-left px-4 py-2 font-semibold">Next period</th>
                <th className="text-right px-4 py-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map(s => (
                <tr key={s.id} className="border-b border-ink-border last:border-0 hover:bg-ink-hover/30">
                  <td className="px-4 py-2 font-medium">{s.tenant_name}</td>
                  <td className="px-4 py-2">{s.product_name}</td>
                  <td className="px-4 py-2">{s.tier_name}</td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      s.status === 'active'    ? 'bg-green-900/40 text-green-300' :
                      s.status === 'trialing'  ? 'bg-blue-900/40 text-blue-300' :
                      s.status === 'past_due'  ? 'bg-amber-900/40 text-amber-300' :
                      'bg-red-900/40 text-red-300'
                    }`}>{s.status}</span>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatMoney(s.price_monthly)}</td>
                  <td className="px-4 py-2 text-ink-muted">{formatDate(s.current_period_end || s.trial_ends_at)}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => setOverrideFor(s)}
                            className="text-xs bg-brand-600 hover:bg-brand-700 text-white font-semibold px-2.5 py-1 rounded">
                      Overrides
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 mt-4">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)}
                    className={`px-3 py-1 text-xs rounded ${
                      p === page ? 'bg-brand-600 text-white' : 'bg-ink-card text-ink-text hover:bg-ink-hover border border-ink-border'
                    }`}>{p}</button>
          ))}
        </div>
      )}

      {overrideFor && (
        <OverrideModal sub={overrideFor} onClose={() => setOverrideFor(null)} onSaved={() => { push('Override applied', 'success'); setOverrideFor(null); }} />
      )}
    </div>
  );
}

function OverrideModal({ sub, onClose, onSaved }: { sub: Subscription; onClose: () => void; onSaved: () => void }) {
  const { push } = useToast();
  const [featureKey, setFeatureKey] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [limitValue, setLimitValue] = useState<string>('');
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [busy, setBusy] = useState(false);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await tenantsApi.addOverride(parseInt(String(sub.tenant_id)), {
        feature_key: featureKey.trim(),
        enabled,
        limit_value: limitValue ? parseInt(limitValue) : null,
        reason: reason.trim() || undefined,
        expires_at: expiresAt || null,
      });
      onSaved();
    } catch (e) { push('Save failed: ' + (e as Error).message, 'error'); }
    finally { setBusy(false); }
  }

  return (
    <Modal open={true} title={`Override · ${sub.tenant_name}`} onClose={onClose}>
      <form onSubmit={save}>
        <div className="mb-3 p-3 bg-ink-bg border border-ink-border rounded text-xs text-ink-muted">
          Entitlement overrides bypass the tier's default. Use for promotions, exceptions, or temporary grants.
          <div className="mt-1">Product: <strong className="text-ink-text">{sub.product_name}</strong> · Tier: <strong className="text-ink-text">{sub.tier_name}</strong></div>
        </div>
        <Field label="Feature key" hint="e.g. invoicing, jobs">
          <input required className={inputCls + ' font-mono'} value={featureKey} onChange={e => setFeatureKey(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Enabled">
            <select className={inputCls} value={enabled ? '1' : '0'} onChange={e => setEnabled(e.target.value === '1')}>
              <option value="1">Yes</option><option value="0">No</option>
            </select>
          </Field>
          <Field label="Limit value" hint="blank for boolean feature">
            <input type="number" min="0" className={inputCls} value={limitValue} onChange={e => setLimitValue(e.target.value)} />
          </Field>
        </div>
        <Field label="Reason"><input className={inputCls} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. goodwill credit after outage" /></Field>
        <Field label="Expires at" hint="ISO date, blank = permanent">
          <input type="datetime-local" className={inputCls} value={expiresAt} onChange={e => setExpiresAt(e.target.value)} />
        </Field>
        <div className="flex gap-2 mt-4">
          <button type="submit" disabled={busy} className="bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2 rounded">
            {busy ? 'Saving…' : 'Apply Override'}
          </button>
          <button type="button" onClick={onClose} className="bg-ink-border hover:bg-ink-border/70 text-ink-text text-sm px-4 py-2 rounded">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}
